<template>
  <div class="bg-gray-50 text-gray-900 min-h-screen p-6 font-sans">
    <div
      id="app"
      class="max-w-[1400px] mx-auto space-y-6 flex flex-col h-[calc(100vh-3rem)]"
    >
      <header class="border-b border-gray-200 pb-4 shrink-0">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              Texture to 3D Render
            </h1>
          </div>
              
          <div class="flex items-center space-x-4">
            <div
              class="flex items-center"
              :class="{'text-brand font-bold': step === 1, 'text-gray-500': step !== 1}"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border-2 mr-2"
                :class="step >= 1 ? 'border-brand bg-brand-light text-brand' : 'border-gray-300'"
              >
                1
              </div>
              Draw Faces
            </div>
            <div
              class="w-12 h-0.5"
              :class="step >= 2 ? 'bg-brand' : 'bg-gray-300'"
            />
            <div
              class="flex items-center"
              :class="{'text-brand font-bold': step === 2, 'text-gray-500': step !== 2}"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border-2 mr-2"
                :class="step >= 2 ? 'border-brand bg-brand-light text-brand' : 'border-gray-300'"
              >
                2
              </div>
              Configure & 3D
            </div>
            <div
              class="w-12 h-0.5"
              :class="step >= 3 ? 'bg-brand' : 'bg-gray-300'"
            />
            <div
              class="flex items-center"
              :class="{'text-brand font-bold': step === 3, 'text-gray-500': step !== 3}"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border-2 mr-2"
                :class="step >= 3 ? 'border-brand bg-brand-light text-brand' : 'border-gray-300'"
              >
                3
              </div>
              Export
            </div>
          </div>
        </div>
      </header>

      <main class="flex-grow flex flex-col overflow-hidden relative mt-2">
        <b-button
          v-if="!uploadedImageSrc" 
          class="btn-group-style custom-secondary-btn mb-2 shadow-none"
          size="sm"
          style="max-width: 150px;"
          @click="openSelectFolderModal"
        >
          <b-icon
            icon="folder"
            class="mr-1"
          />
          <span class="text-sm font-medium">File Browser</span>
        </b-button>
        <SelectFolderModal 
          :mode="'default'"
          @onSelectedItems="fileBrowserItemSelected"
        />
        <div
          v-show="step === 1"
          class="wizard-step flex flex-col h-full w-full relative"
        >
          <!-- Loading Overlay -->
          <div
            v-if="isImageLoading"
            class="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-[100] flex flex-col items-center justify-center rounded-lg"
          >
            <div class="loader-container">
              <div class="brand-spinner" />
            </div>
            <p class="text-xl font-bold text-gray-900 mt-4">
              Processing Texture...
            </p>
            <p class="text-sm text-gray-500 mt-2">
              Uploading and analyzing your image. Please wait.
            </p>
          </div>
          <!-- Toolbar (Only visible when image is uploaded) -->
          <div
            v-if="uploadedImageSrc"
            class="flex flex-wrap gap-4 mb-4 items-center bg-white border border-gray-200 p-4 rounded-lg shadow-sm shrink-0"
          >
            <button
              class="px-4 py-2 border border-gray-300 rounded-md font-semibold hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700"
              @click="triggerFileInput"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Change Texture
            </button>
            <b-button
              class="btn-group-style custom-secondary-btn"
              size="sm"
              @click="openSelectFolderModal"
            >
              <b-icon
                icon="folder"
                class="mr-1"
              />
              <span class="text-sm font-medium">File Browser</span>
            </b-button>
            <button
              class="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md font-semibold transition-colors"
              @click="clearSelection"
            >
              Clear Selection
            </button>
            <div class="ml-4 flex items-center bg-brand-light border border-brand/20 rounded px-3 py-1">
              <span class="text-brand font-bold mr-2">Target Face:</span>
              <span class="text-brand font-medium">{{ currentPromptFace }}</span>
            </div>

            <div class="ml-auto flex items-center space-x-4">
              <div class="text-sm text-gray-500">
                Faces mapped: <span class="font-bold text-gray-900">{{ rectanglesMapped }}</span> / 6
              </div>
              <button
                :disabled="rectanglesMapped < 1 || loadingStates.nextStep"
                class="px-6 py-2 btn-brand rounded-md font-bold transition-colors shadow-md flex items-center gap-2"
                @click="goToStep(2)"
              >
                <span
                  v-if="loadingStates.nextStep"
                  class="button-spinner mr-1"
                />
                <span v-else>Next Step</span>
                <svg
                  v-if="!loadingStates.nextStep"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Main Content Area -->
          <div
            class="bg-white shadow-sm flex-grow flex flex-col relative overflow-hidden border-0"
            :class="{'p-4': uploadedImageSrc, 'p-0': !uploadedImageSrc}"
          >
            <!-- Canvas Viewport (Visible when image is uploaded) -->
            <div
              v-if="uploadedImageSrc" 
              id="canvas-viewport" 
              ref="viewport" 
              class="relative border border-gray-300 rounded bg-[#ebebeb] flex-grow overflow-hidden"
              :class="cursorClass"
              @mousedown="onMouseDown"
              @wheel="onWheel"
            >
              <div class="absolute top-2 right-2 flex flex-col gap-1 z-10 bg-white/80 p-1 rounded shadow-sm">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xl font-bold"
                  @click="applyZoom(1.2)"
                >
                  +
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xl font-bold"
                  @click="applyZoom(0.8)"
                >
                  -
                </button>
              </div>

              <div class="absolute bottom-2 left-2 z-10 text-[10px] text-gray-700 bg-white/90 shadow-sm border border-gray-200 p-1.5 rounded pointer-events-none">
                Scroll: Zoom | Middle-Click: Pan | Left-Click & Drag: Draw
              </div>

              <div
                v-if="showOverlayPrompt" 
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 
                                  text-5xl font-extrabold text-gray-900 text-center drop-shadow-md
                                  bg-white/80 px-6 py-4 rounded-xl fade-out border border-gray-200"
              >
                {{ currentPromptFace }}
              </div>

              <div
                class="absolute top-0 left-0 origin-top-left"
                :style="transformStyle"
              >
                <canvas
                  ref="bgCanvas"
                  class="block"
                />
                <canvas
                  ref="drawCanvas"
                  class="absolute top-0 left-0 pointer-events-none"
                />
              </div>
            </div>

            <!-- Drag & Drop Zone (Visible when NO image is uploaded) -->
            <div
              v-if="!isImageLoading && !uploadedImageSrc"
              class="flex-grow flex flex-col items-center justify-center p-12 transition-all cursor-pointer group rounded-lg"
              :class="isDragging ? 'bg-brand-light border-2 border-brand border-dashed' : 'bg-gray-50 border-2 border-gray-200 border-dashed hover:border-brand'"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
              @click="triggerFileInput"
            >
              <div class="w-20 h-20 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-10 h-10 text-brand"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
                       
              <h3 class="text-2xl font-bold text-gray-900 mb-2">
                Upload Source File
              </h3>
              <p class="text-gray-500 text-center max-w-sm mb-8">
                Drag and drop your image here, or <span class="text-brand font-bold underline">click to browse</span>.
                Supports JPG, PNG and other image formats.
              </p>
            </div>

            <!-- Hidden Input -->
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onFileChange"
            >
          </div>
        </div>

        <div
          v-show="step === 2"
          class="wizard-step flex flex-col h-full w-full"
        >
          <div class="flex justify-between mb-4 shrink-0">
            <button
              class="px-4 py-2 text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-md font-semibold transition-colors flex items-center gap-2"
              @click="goToStep(1)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
            <div class="flex items-center space-x-4">
              <button
                class="px-4 py-2 text-white bg-brand hover:bg-brand-dark rounded-md font-semibold transition-colors flex items-center gap-2 shadow-md"
                @click="exportMappings"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
                Export Mappings
              </button>
              <button
                :disabled="loadingStates.reviewExport"
                class="px-6 py-2 bg-brand hover:bg-brand-dark text-white rounded-md font-bold transition-colors shadow-md flex items-center gap-2"
                @click="goToStep(3)"
              >
                <span
                  v-if="loadingStates.reviewExport"
                  class="button-spinner mr-1"
                />
                <span v-else>Review & Export</span>
                <svg
                  v-if="!loadingStates.reviewExport"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-12 gap-6 flex-grow min-h-0">
            <div class="md:col-span-5 bg-white border border-gray-200 p-4 rounded-lg shadow-sm flex flex-col overflow-y-auto">
              <div class="flex items-center justify-between mb-4 border-b border-gray-200 pb-2">
                <h2 class="text-xl font-semibold text-gray-900">
                  Reassign and Rotate Faces
                </h2>
                <button
                  type="button"
                  class="tdr-face-export-btn tdr-face-export-all-btn shrink-0 flex items-center justify-center text-gray-700"
                  title="Download all faces (zip)"
                  :disabled="loadingStates.facesZip"
                  @click="exportAllFaceTexturesZip"
                >
                  <span
                    v-if="loadingStates.facesZip"
                    class="button-spinner"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
                      
              <div class="space-y-4">
                <div
                  v-for="(face, index) in faceConfigs"
                  :key="face.role"
                  class="tdr-face-config-row bg-gray-50 p-3 rounded border border-gray-200 flex flex-col gap-2 relative"
                >
                  <div class="flex justify-between items-center gap-2 min-h-[2rem]">
                    <span class="font-bold text-gray-700">{{ face.role }}</span>
                  </div>
                  <div class="flex items-center gap-3 flex-nowrap">
                    <div class="flex-grow flex flex-col min-w-0">
                      <div class="custom-dropdown-container relative">
                        <button
                          class="w-full h-9 bg-white text-gray-900 border border-gray-300 rounded-md px-2 text-sm flex items-center justify-between hover:border-brand transition-colors dropdown-toggle-custom" 
                          @click="toggleDropdown(index)"
                        >
                          <div class="flex items-center gap-2 overflow-hidden">
                            <div
                              v-if="face.rectIndex !== null"
                              class="w-6 h-6 rounded border border-gray-200 overflow-hidden shrink-0"
                            >
                              <img
                                :src="getFaceThumb(face.rectIndex, 0)"
                                class="w-full h-full object-cover"
                              >
                            </div>
                            <div
                              v-else
                              class="w-6 h-6 rounded border border-gray-200 bg-gray-50 flex items-center justify-center shrink-0"
                            >
                              <span class="text-[8px] text-gray-400 font-bold uppercase">N/A</span>
                            </div>
                            <span class="truncate font-medium">
                              {{ face.rectIndex !== null ? `Face ${face.rectIndex + 1} (${rolePromptOrder[face.rectIndex]})` : 'None' }}
                            </span>
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4 text-gray-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </button>

                        <div
                          v-if="activeDropdown === index"
                          class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-y-auto max-h-60 custom-dropdown-menu"
                        >
                          <div
                            class="p-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors dropdown-item-custom" 
                            :class="{'bg-brand-light border-l-4 border-l-brand': face.rectIndex === null}"
                            @click="selectFace(index, null)"
                          >
                            <div class="w-10 h-10 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 italic text-xs shrink-0">
                              None
                            </div>
                            <span class="text-sm font-bold text-gray-500">No texture (Wireframe)</span>
                          </div>
                          <div
                            v-for="(rect, rIdx) in rectangles" 
                            :key="rIdx" 
                            class="p-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors dropdown-item-custom"
                            :class="{'bg-brand-light border-l-4 border-l-brand': face.rectIndex === rIdx}"
                            @click="selectFace(index, rIdx)"
                          >
                            <div class="w-10 h-10 rounded border border-gray-300 overflow-hidden shrink-0 bg-white shadow-sm">
                              <img
                                :src="getFaceThumb(rIdx, 0)"
                                class="w-full h-full object-cover"
                              >
                            </div>
                            <div class="flex flex-col min-w-0">
                              <span class="text-sm font-bold text-gray-900 truncate">Face {{ rIdx + 1 }}</span>
                              <span class="text-[10px] text-gray-500 font-medium uppercase truncate">{{ rolePromptOrder[rIdx] }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="tdr-face-actions shrink-0">
                      <button
                        type="button"
                        class="tdr-face-export-btn tdr-face-export-btn-float shrink-0 flex items-center justify-center text-gray-700"
                        title="Download this face (full resolution)"
                        :disabled="face.rectIndex === null"
                        :class="{ 'is-disabled': face.rectIndex === null }"
                        @click.stop="face.rectIndex === null ? null : exportFaceTexture(index)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>

                      <div class="flex items-center gap-2">
                        <button
                          class="tdr-face-action-btn"
                          title="Rotate 90° Left"
                          @click="rotateFaceConfig(index, -90)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                            <path d="M3 3v5h5" />
                          </svg>
                        </button>
                        <button
                          class="tdr-face-action-btn"
                          title="Rotate 90° Right"
                          @click="rotateFaceConfig(index, 90)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                            <path d="M21 3v5h-5" />
                          </svg>
                        </button>
                        <button
                          class="tdr-face-action-btn"
                          :class="{ 'is-active': face.flipX }"
                          title="Flip Horizontally"
                          @click="flipFaceConfig(index, 'x')"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 7H5a2 2 0 00-2 2v6a2 2 0 002 2h3m8-10h3a2 2 0 012 2v6a2 2 0 01-2 2h-3M12 4v16"
                            />
                          </svg>
                        </button>
                        <button
                          class="tdr-face-action-btn"
                          :class="{ 'is-active': face.flipY }"
                          title="Flip Vertically"
                          @click="flipFaceConfig(index, 'y')"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17 8v-3a2 2 0 00-2-2H9a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h6a2 2 0 002-2v-3M4 12h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="md:col-span-7 bg-white border border-gray-200 p-4 rounded-lg shadow-sm flex flex-col relative w-full h-full min-h-[400px]">
              <h2 class="text-xl font-semibold mb-2 text-gray-900">
                3D Preview
              </h2>
              <div
                ref="threeContainerStep2"
                class="w-full aspect-video bg-[#ebebeb] rounded border border-gray-200 overflow-hidden relative mx-auto max-w-full"
              >
                <div class="absolute top-2 left-2 z-10 text-[10px] text-gray-700 bg-white/90 border border-gray-200 p-2 rounded pointer-events-none font-mono shadow-sm">
                  <div>OBJ POS: {{ meshStats.pos.x }}, {{ meshStats.pos.y }}, {{ meshStats.pos.z }}</div>
                  <div>OBJ ROT: {{ meshStats.rot.x }}°, {{ meshStats.rot.y }}°, {{ meshStats.rot.z }}°</div>
                  <div>CAM ZOOM: {{ camStats.zoom }}x</div>
                  <div>CAM FOV: {{ camStats.fov }}°</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-show="step === 3"
          class="wizard-step flex flex-col h-full w-full"
        >
          <div class="flex justify-between mb-4 shrink-0">
            <button
              class="px-4 py-2 text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-md font-semibold transition-colors flex items-center gap-2"
              @click="goToStep(2)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
            <div class="flex gap-4">
              <button
                class="px-4 py-2 text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-md font-semibold transition-colors flex items-center gap-2 shadow-sm"
                @click="reset3DView"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 110 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clip-rule="evenodd"
                  />
                </svg>
                Reset Camera
              </button>
              <button
                class="px-4 py-2 text-white bg-brand hover:bg-brand-dark rounded-md font-semibold transition-colors flex items-center gap-2 shadow-md"
                @click="openVideoModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
                Export as Video
              </button>
              <button
                class="px-6 py-2 btn-brand rounded-md font-bold transition-colors shadow-md flex items-center gap-2"
                @click="export3DImage"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
                Export as Image
              </button>
            </div>
          </div>

          <div class="bg-white border border-gray-200 p-4 rounded-lg shadow-sm flex-grow flex flex-col relative w-full h-full min-h-[400px]">
            <h2 class="text-xl font-semibold mb-2 flex items-center justify-between text-gray-900">
              <span>3D Preview</span>
            </h2>
            <div
              ref="threeContainerStep3"
              class="w-full aspect-video bg-[#ebebeb] rounded border border-gray-200 overflow-hidden relative mx-auto max-w-full"
            >
              <div class="absolute top-2 left-2 z-10 text-[10px] text-gray-700 bg-white/90 border border-gray-200 p-2 rounded pointer-events-none font-mono shadow-sm">
                <div>OBJ POS: {{ meshStats.pos.x }}, {{ meshStats.pos.y }}, {{ meshStats.pos.z }}</div>
                <div>OBJ ROT: {{ meshStats.rot.x }}°, {{ meshStats.rot.y }}°, {{ meshStats.rot.z }}°</div>
                <div>CAM ZOOM: {{ camStats.zoom }}x</div>
                <div>CAM FOV: {{ camStats.fov }}°</div>
              </div>
              <div class="absolute top-2 right-2 flex flex-col gap-1 z-10 bg-white/80 border border-gray-200 p-1 rounded shadow-sm">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xl font-bold"
                  title="Zoom In"
                  @click="zoom3DIn"
                >
                  +
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xl font-bold"
                  title="Zoom Out"
                  @click="zoom3DOut"
                >
                  -
                </button>
                <hr class="border-gray-300 my-1">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate +X"
                  @click="rotateMesh('x', 1)"
                >
                  +X
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate -X"
                  @click="rotateMesh('x', -1)"
                >
                  -X
                </button>
                <hr class="border-gray-300 my-1">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate +Y"
                  @click="rotateMesh('y', 1)"
                >
                  +Y
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate -Y"
                  @click="rotateMesh('y', -1)"
                >
                  -Y
                </button>
                <hr class="border-gray-300 my-1">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate +Z"
                  @click="rotateMesh('z', 1)"
                >
                  +Z
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
                  title="Rotate -Z"
                  @click="rotateMesh('z', -1)"
                >
                  -Z
                </button>
                <hr class="border-gray-300 my-1">
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-[10px] font-bold"
                  title="Increase FOV"
                  @click="changeFOV(5)"
                >
                  FOV+
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-[10px] font-bold"
                  title="Decrease FOV"
                  @click="changeFOV(-5)"
                >
                  FOV-
                </button>
              </div>
            </div>
          </div>
        </div>

        <input
          ref="mappingFileInput"
          type="file"
          accept=".json"
          class="hidden"
          @change="onMappingFileChange"
        >

        <div
          v-if="showVideoModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm"
        >
          <div class="bg-white border border-gray-200 rounded-xl p-6 max-w-md w-full shadow-2xl space-y-5">
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-brand"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
                Export as Video
              </h3>
              <button
                class="text-gray-400 hover:text-gray-700"
                :disabled="isVideoGenerating"
                @click="showVideoModal = false"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div
              v-if="!isVideoGenerating"
              class="space-y-4"
            >
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Resolution</label>
                <div class="grid grid-cols-3 gap-2 mb-3">
                  <button
                    v-for="res in [{l:'1K', w:1920, h:1080}, {l:'2K', w:2560, h:1440}, {l:'4K', w:3840, h:2160}]"
                    :key="res.l"
                    class="py-1.5 px-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 text-xs font-bold transition-colors"
                    @click="setResolution(res.w, res.h)"
                  >
                    {{ res.l }}
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="text-[10px] uppercase font-bold text-gray-400 block mb-1">Width</label>
                    <input
                      v-model.number="videoSettings.width"
                      type="number"
                      class="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand"
                    >
                  </div>
                  <div>
                    <label class="text-[10px] uppercase font-bold text-gray-400 block mb-1">Height</label>
                    <input
                      v-model.number="videoSettings.height"
                      type="number"
                      class="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-brand"
                    >
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Rotation Axis</label>
                <div class="grid grid-cols-3 gap-2">
                  <button
                    v-for="axis in ['x', 'y', 'z']"
                    :key="axis" 
                    :class="['py-2 px-3 rounded-md text-sm font-bold capitalize transition-colors', 
                             videoSettings.axis === axis ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200']"
                    @click="videoSettings.axis = axis"
                  >
                    {{ axis }}
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1">Duration (seconds)</label>
                  <input
                    v-model.number="videoSettings.duration"
                    type="number"
                    min="1"
                    max="60"
                    class="w-full bg-white border border-gray-300 rounded px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1">Turns</label>
                  <input
                    v-model.number="videoSettings.loops"
                    type="number"
                    min="1"
                    max="20"
                    class="w-full bg-white border border-gray-300 rounded px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Background Color</label>
                <div class="grid grid-cols-2 gap-3">
                  <button
                    :class="['py-2 px-3 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-3 bg-white border', 
                             videoSettings.background === 'white' ? 'ring-2 ring-brand border-transparent text-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50']"
                    @click="videoSettings.background = 'white'"
                  >
                    <span class="w-4 h-4 rounded-full bg-white shadow-sm ring-1 ring-black/10" />
                    White
                  </button>
                  <button
                    :class="['py-2 px-3 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-3 bg-white border', 
                             videoSettings.background === 'black' ? 'ring-2 ring-brand border-transparent text-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50']"
                    @click="videoSettings.background = 'black'"
                  >
                    <span class="w-4 h-4 rounded-full bg-black shadow-sm ring-1 ring-black/10" />
                    Black
                  </button>
                </div>
              </div>

              <button
                class="w-full py-3 bg-brand hover:bg-brand-dark rounded-lg font-bold text-white transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-md flex items-center justify-center gap-2"
                @click="generateVideo"
              >
                Generate Video
              </button>
            </div>

            <div
              v-else
              class="space-y-6 py-4"
            >
              <div class="text-center">
                <div class="text-sm text-gray-500 mb-2">
                  Rendering Video...
                </div>
                <div class="text-2xl font-bold text-brand">
                  {{ Math.round(videoProgress) }}%
                </div>
              </div>
                      
              <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden border border-gray-300">
                <div
                  class="bg-brand h-full transition-all duration-300"
                  :style="{ width: videoProgress + '%' }"
                />
              </div>
                      
              <button
                class="w-full py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition-colors"
                @click="abortVideoGeneration"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="showRecoveryModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm"
        >
          <div class="bg-white border border-gray-200 rounded-xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 class="text-xl font-bold text-gray-900">
              Restore Mappings?
            </h3>
            <p class="text-gray-600">
              Previous mapping configuration found for this image. How would you like to proceed?
            </p>
                  
            <div class="space-y-3 pt-2">
              <button
                :disabled="!tempMappingData || loadingStates.loadSaved" 
                :class="['w-full py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2',
                         tempMappingData && !loadingStates.loadSaved ? 'bg-brand hover:bg-brand-dark text-white shadow-md' : 'bg-gray-200 text-gray-400 cursor-not-allowed']" 
                @click="loadSavedMapping"
              >
                <span
                  v-if="loadingStates.loadSaved"
                  class="button-spinner"
                />
                <span v-else>Load Saved (Browser)</span>
              </button>
              <button
                :disabled="loadingStates.loadFile" 
                class="w-full py-3 bg-brand hover:bg-brand-dark text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 shadow-md"
                @click="triggerMappingFileClick"
              >
                <span
                  v-if="loadingStates.loadFile"
                  class="button-spinner"
                />
                <span v-else>Upload File (.json)</span>
              </button>
              <button
                class="w-full py-3 bg-brand hover:bg-brand-dark text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 shadow-md"
                @click="openSelectFolderModalForMappingJson"
              >
                <span>Load From File Browser</span>
              </button>
              <button
                class="w-full py-3 text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                @click="cancelRecovery"
              >
                <span>Cancel (New Mapping)</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch, getCurrentInstance  } from 'vue';
import * as THREE from 'three';
import CryptoJS from 'crypto-js';
import JSZip from 'jszip';
import { FILE_TYPE } from '@frontend/constants/file-image-types';
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";

export default {
    name: 'ThreeDRendersPage',
    components: {
      SelectFolderModal
    },
    setup() {
        const { proxy } = getCurrentInstance();
        const step = ref(1);

        // Step 1 UI
        const fileInput = ref(null);
        const viewport = ref(null);
        const bgCanvas = ref(null);
        const drawCanvas = ref(null);
        const threeContainerStep2 = ref(null);
        const threeContainerStep3 = ref(null);

        const rectangles = ref([]); // {x, y, w, h}
        const scale = ref(1);
        const panX = ref(0);
        const panY = ref(0);
        const isPanning = ref(false);

        // Sub-wizard drawing sequence
        const rolePromptOrder = ['Top', 'Front', 'Bottom', 'Back', 'Right', 'Left'];
        const uploadedImageSrc = ref(false); // Indicates image loaded successfully
        const showOverlayPrompt = ref(false);
        const overlayKey = ref(0); // Used to re-trigger CSS animation
        const currentImageMd5 = ref(null); // To store current image hash
        const isDragging = ref(false);
        
        const currentPromptFace = computed(() => {
            const ordinals = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];
            const count = rectangles.value.length;
            if (count < 6) return `Select ${ordinals[count]} face`;
            return 'Analysis Complete';
        });

        const camStats = reactive({ zoom: '0.54', fov: '10' });
        const meshStats = reactive({ 
            pos: { x: '0.08', y: '0.14', z: '0.00' },
            rot: { x: '90', y: '0', z: '-15' }
        });

        // Mapping persistence / Modal
        const showRecoveryModal = ref(false);
        const tempMappingData = ref(null);
        const mappingFileInput = ref(null);
        const expectingMappingJsonFromBrowser = ref(false);

        // Video Export
        const showVideoModal = ref(false);
        const isVideoGenerating = ref(false);
        const videoProgress = ref(0);
        const videoSettings = reactive({
            axis: 'z',
            duration: 5,
            loops: 1,
            background: 'white',
            width: 1920,
            height: 1080
        });

        // Maintain 16:9 Aspect Ratio
        watch(() => videoSettings.width, (newW) => {
            const calculatedH = Math.round(newW * 1080 / 1920);
            if (videoSettings.height !== calculatedH) {
                videoSettings.height = calculatedH;
            }
        });

        watch(() => videoSettings.height, (newH) => {
            const calculatedW = Math.round(newH * 1920 / 1080);
            if (videoSettings.width !== calculatedW) {
                videoSettings.width = calculatedW;
            }
        });

        const setResolution = (w, h) => {
            videoSettings.width = w;
            videoSettings.height = h;
        };
        const activeDropdown = ref(null);
        const isImageLoading = ref(false);
        const loadingStates = reactive({
            nextStep: false,
            reviewExport: false,
            loadSaved: false,
            loadFile: false,
            facesZip: false
        });
        let isAbortingVideo = false;

        const openVideoModal = () => {
            showVideoModal.value = true;
            isVideoGenerating.value = false;
            videoProgress.value = 0;
            isAbortingVideo = false;
        };

        const abortVideoGeneration = () => {
            isAbortingVideo = true;
            isVideoGenerating.value = false;
        };

        const generateVideo = async () => {
            if (!scene || !camera || !renderer || !cubeMesh) return;
            
            isVideoGenerating.value = true;
            videoProgress.value = 0;
            isAbortingVideo = false;

            // 1. Prepare High-Res Recording Canvas
            const originalSize = new THREE.Vector2();
            renderer.getSize(originalSize);
            
            // Apply user settings resolution
            const recWidth = videoSettings.width;
            const recHeight = videoSettings.height;
            renderer.setSize(recWidth, recHeight, false);
            camera.aspect = recWidth / recHeight;
            camera.updateProjectionMatrix();

            const stream = renderer.domElement.captureStream(60); // 60 FPS
            
            // Support detection for different browsers
            const mimeType = [
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp9',
                'video/webm;codecs=vp8',
                'video/webm',
                'video/mp4'
            ].find(type => MediaRecorder.isTypeSupported(type));

            if (!mimeType) {
                alert("Your browser does not support any of the required video recording formats.");
                cleanup();
                return;
            }

            const recorder = new MediaRecorder(stream, {
                mimeType,
                videoBitsPerSecond: 10000000 // 10 Mbps
            });

            const chunks = [];
            recorder.ondataavailable = (e) => chunks.push(e.data);
            
            // Store original state to restore later
            const startPos = cubeMesh.position.clone();
            const startRot = cubeMesh.rotation.clone();
            const totalFrames = videoSettings.duration * 60;
            const totalRotation = videoSettings.loops * Math.PI * 2;
            
            let frame = 0;
            recorder.start();

            const recordFrame = () => {
                if (isAbortingVideo) {
                    recorder.stop();
                    cleanup();
                    return;
                }

                if (frame < totalFrames) {
                    const progress = frame / totalFrames;
                    const currentRot = progress * totalRotation;
                    
                    // Apply rotation based on axis
                    if (videoSettings.axis === 'x') cubeMesh.rotation.x = startRot.x + currentRot;
                    else if (videoSettings.axis === 'y') cubeMesh.rotation.y = startRot.y + currentRot;
                    else if (videoSettings.axis === 'z') cubeMesh.rotation.z = startRot.z + currentRot;
                    
                    // Apply background color for recording
                    const bgColor = videoSettings.background === 'white' ? 0xffffff : 0x000000;
                    renderer.setClearColor(bgColor, 1);
                    scene.background = new THREE.Color(bgColor);

                    renderer.render(scene, camera);
                    videoProgress.value = progress * 100;
                    
                    frame++;
                    requestAnimationFrame(recordFrame);
                } else {
                    recorder.stop();
                }
            };

            recorder.onstop = () => {
                if (!isAbortingVideo) {
                    const blob = new Blob(chunks, { type: 'video/webm' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `3d_render_${Date.now()}.webm`;
                    link.click();
                    URL.revokeObjectURL(url);
                }
                cleanup();
            };

            const cleanup = () => {
                // Restore original state
                renderer.setSize(originalSize.x, originalSize.y);
                renderer.setClearColor(0x000000, 0); // Transparent default
                scene.background = null;
                camera.aspect = originalSize.x / originalSize.y;
                camera.updateProjectionMatrix();
                cubeMesh.position.copy(startPos);
                cubeMesh.rotation.copy(startRot);
                
                isVideoGenerating.value = false;
                showVideoModal.value = false;
            };

            recordFrame();
        };

        watch(currentPromptFace, (newVal) => {
            if (newVal !== 'done' && uploadedImageSrc.value) {
                showOverlayPrompt.value = false;
                setTimeout(() => { showOverlayPrompt.value = true; }, 50); // Small delay to re-trigger
            } else {
                showOverlayPrompt.value = false;
            }
        });

        // Default rotations mapping
        const defaultRotations = {
            'Front': 0,
            'Back': 0,
            'Top': 180,
            'Bottom': 0,
            'Right': 90,
            'Left': -90
        };

        // Step 2 Logic
        const roles = ['Right', 'Left', 'Top', 'Bottom', 'Front', 'Back']; // THREE sequence
        
        const faceConfigs = ref([
            { role: 'Right', rectIndex: null, rotation: 0, flipX: false, flipY: false },
            { role: 'Left', rectIndex: null, rotation: 0, flipX: false, flipY: false },
            { role: 'Front', rectIndex: null, rotation: 0, flipX: false, flipY: false },
            { role: 'Back', rectIndex: null, rotation: 0, flipX: false, flipY: false },
            { role: 'Bottom', rectIndex: null, rotation: 0, flipX: false, flipY: false },
            { role: 'Top', rectIndex: null, rotation: 0, flipX: false, flipY: false }
        ]);
        
        // Internal canvas states
        let bgCtx, drawCtx;
        let uploadedImage = new Image();
        let isDrawing = false;
        let startImgX = 0, startImgY = 0; 
        let startPanX = 0, startPanY = 0; 
        
        // 3D/Texture variables
        let scene, camera, renderer, cubeMesh, animationId;
        let currentRotationX = THREE.MathUtils.degToRad(90);
        let currentRotationY = THREE.MathUtils.degToRad(0);
        let currentRotationZ = THREE.MathUtils.degToRad(-15);
        let currentPositionX = 0.08;
        let currentPositionY = 0.14;
        let currentPositionZ = 0.00;
        let currentZoom = 0.54;
        let currentFov = 10;
        let isDraggingObject = false;
        let dragMode = null; // 'rotate' or 'pan'
        let prevMouseX = 0, prevMouseY = 0;
        const cachedThumbs = reactive({});

        const transformStyle = computed(() => {
            return `transform: translate(${panX.value}px, ${panY.value}px) scale(${scale.value})`;
        });

        const cursorClass = computed(() => {
            return isPanning.value ? 'cursor-grabbing' : 'cursor-crosshair';
        });

        onMounted(() => {
            if (bgCanvas.value) bgCtx = bgCanvas.value.getContext('2d', { willReadFrequently: true });
            if (drawCanvas.value) drawCtx = drawCanvas.value.getContext('2d');
            
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('resize', onWindowResize);
        });

        onUnmounted(() => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('resize', onWindowResize);
            if (animationId) cancelAnimationFrame(animationId);
        });

        const onFileChange = (e) => {
            let file = null;
            if (e.target && e.target.files && e.target.files.length > 0) {
                file = e.target.files[0];
            } else if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                file = e.dataTransfer.files[0];
            } else if (e instanceof File) {
                file = e;
            }

            if (!file) return;
            
            isImageLoading.value = true;
            
            // First calculate MD5 hash of the file
            const hashReader = new FileReader();
            hashReader.onerror = () => { isImageLoading.value = false; };
            hashReader.onload = (hashEvent) => {
                const wordArray = CryptoJS.lib.WordArray.create(hashEvent.target.result);
                const md5hash = CryptoJS.MD5(wordArray).toString();
                currentImageMd5.value = md5hash;
                
                // Proceed to load the image for canvas
                const imgReader = new FileReader();
                imgReader.onerror = () => { isImageLoading.value = false; };
                imgReader.onload = (event) => { 
                    uploadedImage.onload = () => handleImageLoad(md5hash);
                    uploadedImage.onerror = () => { isImageLoading.value = false; };
                    uploadedImage.src = event.target.result; 
                };
                imgReader.readAsDataURL(file);
            };
            hashReader.readAsArrayBuffer(file);
        };

        const onDrop = (e) => {
            isDragging.value = false;
            onFileChange(e);
        };

        const onDragOver = (e) => {
            isDragging.value = true;
        };

        const onDragLeave = () => {
            isDragging.value = false;
        };

        const triggerFileInput = () => {
            if (fileInput.value) fileInput.value.click();
        };

        const handleImageLoad = async (md5hash) => {
            uploadedImageSrc.value = true;
            
            // Wait for DOM to update and render canvas elements
            await nextTick();
            
            if (!bgCanvas.value || !drawCanvas.value) {
                console.error("Canvas refs not found after nextTick");
                isImageLoading.value = false;
                return;
            }

            bgCanvas.value.width = drawCanvas.value.width = uploadedImage.width;
            bgCanvas.value.height = drawCanvas.value.height = uploadedImage.height;

            // Ensure contexts exist before drawing
            bgCtx = bgCanvas.value.getContext('2d', { willReadFrequently: true });
            drawCtx = drawCanvas.value.getContext('2d');
            bgCtx.drawImage(uploadedImage, 0, 0);
            
            const vRect = viewport.value.getBoundingClientRect();
            const scaleX = vRect.width / uploadedImage.width;
            const scaleY = vRect.height / uploadedImage.height;
            scale.value = Math.min(scaleX, scaleY, 1) * 0.95; 

            panX.value = (vRect.width - (uploadedImage.width * scale.value)) / 2;
            panY.value = (vRect.height - (uploadedImage.height * scale.value)) / 2;
            
            // Check if mapping exists in localStorage
            const savedMapping = localStorage.getItem(`3d_mapping_${md5hash}`);
            tempMappingData.value = null;
            if (savedMapping) {
                try {
                    tempMappingData.value = JSON.parse(savedMapping);
                } catch (e) {
                    console.error("Error parsing saved mapping:", e);
                }
            }
            
            isImageLoading.value = false;
            showRecoveryModal.value = true;
            // Always clear selection before recovery choice
            clearSelection(); 
        };

        const applyMappingData = (data) => {
            if (!data) return;
            
            try {
                rectangles.value = data.rectangles || [];
                if (data.faceConfigs) {
                    for (let i = 0; i < 6; i++) {
                        faceConfigs.value[i].rectIndex = data.faceConfigs[i].rectIndex;
                        faceConfigs.value[i].rotation = data.faceConfigs[i].rotation;
                        faceConfigs.value[i].flipX = data.faceConfigs[i].flipX || false;
                        faceConfigs.value[i].flipY = data.faceConfigs[i].flipY || false;
                    }
                }
                
                // 3D parameters
                if (data.viewSettings) {
                    currentPositionX = data.viewSettings.pos?.x ?? 0.08;
                    currentPositionY = data.viewSettings.pos?.y ?? 0.14;
                    currentPositionZ = data.viewSettings.pos?.z ?? 0.00;
                    
                    currentRotationX = THREE.MathUtils.degToRad(data.viewSettings.rot?.x ?? 90);
                    currentRotationY = THREE.MathUtils.degToRad(data.viewSettings.rot?.y ?? 0);
                    currentRotationZ = THREE.MathUtils.degToRad(data.viewSettings.rot?.z ?? -15);
                    
                    currentZoom = data.viewSettings.camZoom ?? 0.54;
                    currentFov = data.viewSettings.camFov ?? 10;

                    if (camera) {
                        camera.zoom = currentZoom;
                        camera.fov = currentFov;
                        camera.updateProjectionMatrix();
                    }
                } else {
                    // Apply defaults if no view settings
                    currentPositionX = 0.08;
                    currentPositionY = 0.14;
                    currentPositionZ = 0.00;
                    currentRotationX = THREE.MathUtils.degToRad(90);
                    currentRotationY = THREE.MathUtils.degToRad(0);
                    currentRotationZ = THREE.MathUtils.degToRad(-15);
                    currentZoom = 0.54;
                    currentFov = 10;
                }
                
                clearCache();
                redrawRectangles();
                showRecoveryModal.value = false;
                goToStep(2); 
            } catch (e) {
                alert("Error applying mapping data: " + e.message);
            }
        };

        const loadSavedMapping = async () => {
            loadingStates.loadSaved = true;
            try {
                await applyMappingData(tempMappingData.value);
            } finally {
                loadingStates.loadSaved = false;
            }
        };

        const cancelRecovery = () => {
            clearSelection();
            showRecoveryModal.value = false;
        };

        const triggerMappingFileClick = () => {
            mappingFileInput.value.click();
        };

        const onMappingFileChange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            
            loadingStates.loadFile = true;
            const reader = new FileReader();
            reader.onerror = () => { loadingStates.loadFile = false; };
            reader.onload = async (event) => {
                try {
                    const data = JSON.parse(event.target.result);
                    if (data.md5 && data.md5 !== currentImageMd5.value) {
                        if (!confirm("The selected mapping file was created for a different image (MD5 mismatch). Load anyway?")) {
                            loadingStates.loadFile = false;
                            return;
                        }
                    }
                    await applyMappingData(data);
                } catch (err) {
                    alert("Invalid mapping file: " + err.message);
                } finally {
                    loadingStates.loadFile = false;
                }
            };
            reader.readAsText(file);
        };

        const exportMappings = () => {
            if (!currentImageMd5.value) return;
            
            const data = {
                md5: currentImageMd5.value,
                rectangles: rectangles.value,
                faceConfigs: faceConfigs.value.map(fc => ({ 
                    rectIndex: fc.rectIndex, 
                    rotation: fc.rotation, 
                    flipX: fc.flipX, 
                    flipY: fc.flipY 
                })),
                viewSettings: {
                    pos: { x: currentPositionX, y: currentPositionY, z: currentPositionZ },
                    rot: { 
                        x: Math.round(THREE.MathUtils.radToDeg(currentRotationX)), 
                        y: Math.round(THREE.MathUtils.radToDeg(currentRotationY)), 
                        z: Math.round(THREE.MathUtils.radToDeg(currentRotationZ)) 
                    },
                    camZoom: camera.zoom,
                    camFov: camera.fov
                }
            };
            
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `3d_mappings_${currentImageMd5.value.substring(0, 8)}.json`;
            link.click();
            URL.revokeObjectURL(url);
        };

        const getImgCoords = (e) => {
            const rect = viewport.value.getBoundingClientRect();
            return {
                x: ((e.clientX - rect.left) - panX.value) / scale.value,
                y: ((e.clientY - rect.top) - panY.value) / scale.value
            };
        };

        const onMouseDown = (e) => {
            if (step.value !== 1) return;
            
            if (e.button === 1) { 
                e.preventDefault();
                isPanning.value = true;
                startPanX = e.clientX - panX.value;
                startPanY = e.clientY - panY.value;
                return;
            }

            if (e.button === 0) { 
                if (rectangles.value.length >= 6) return;
                if (!drawCanvas.value || !viewport.value) return;
                
                // Re-init context if missing (e.g. after re-renders)
                if (!drawCtx) drawCtx = drawCanvas.value.getContext('2d');
                
                isDrawing = true;
                const coords = getImgCoords(e);
                startImgX = coords.x;
                startImgY = coords.y;
            }
        };

        const onMouseMove = (e) => {
            if (step.value !== 1) return;

            if (isPanning.value) {
                panX.value = e.clientX - startPanX;
                panY.value = e.clientY - startPanY;
            } else if (isDrawing) {
                if (!drawCtx) return;
                const coords = getImgCoords(e);
                redrawRectangles();
                
                drawCtx.strokeStyle = '#3b82f6'; 
                drawCtx.lineWidth = 2 / scale.value; 
                drawCtx.setLineDash([5 / scale.value, 5 / scale.value]);
                drawCtx.strokeRect(startImgX, startImgY, coords.x - startImgX, coords.y - startImgY);
            }
        };

        const onMouseUp = (e) => {
            if (step.value !== 1) return;

            if (e.button === 1) {
                isPanning.value = false;
            } else if (e.button === 0 && isDrawing) {
                isDrawing = false;
                
                const coords = getImgCoords(e);
                const x = Math.min(startImgX, coords.x);
                const y = Math.min(startImgY, coords.y);
                const w = Math.abs(coords.x - startImgX);
                const h = Math.abs(coords.y - startImgY);

                if (w > 10 && h > 10) {
                    rectangles.value.push({ x, y, w, h });
                    
                    if (rectangles.value.length === 6) {
                        autoAssignRoles();
                    }
                    
                    clearCache();
                    saveMappingToStorage();
                }
                redrawRectangles();
            }
        };

        const applyZoom = (zoomFactor, pointerX, pointerY) => {
            if (step.value !== 1 || !viewport.value) return;
            const rect = viewport.value.getBoundingClientRect();
            const cx = pointerX !== undefined ? pointerX - rect.left : rect.width / 2;
            const cy = pointerY !== undefined ? pointerY - rect.top : rect.height / 2;

            const newScale = Math.max(0.05, Math.min(scale.value * zoomFactor, 15));
            const actualFactor = newScale / scale.value;

            panX.value = cx - (cx - panX.value) * actualFactor;
            panY.value = cy - (cy - panY.value) * actualFactor;
            scale.value = newScale;
            redrawRectangles(); 
        };

        const onWheel = (e) => {
            if (step.value !== 1) return;
            e.preventDefault();
            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            applyZoom(factor, e.clientX, e.clientY);
        };

        const clearSelection = () => {
            rectangles.value = [];
            clearCache();
            redrawRectangles();
            
            // Reset config
            faceConfigs.value.forEach(fc => {
                fc.rectIndex = null;
                fc.rotation = 0;
                fc.flipX = false;
                fc.flipY = false;
            });
            
            // Resets indicator text via watch
            showOverlayPrompt.value = false;
            if(uploadedImageSrc.value) {
                setTimeout(() => showOverlayPrompt.value = true, 50);
            }
            
            saveMappingToStorage();
        };

        const redrawRectangles = () => {
            if (!drawCtx || !drawCanvas.value) return;
            drawCtx.clearRect(0, 0, drawCanvas.value.width, drawCanvas.value.height);
            drawCtx.setLineDash([]);
            drawCtx.lineWidth = 2 / scale.value; 
            
            rectangles.value.forEach((r, index) => {
                drawCtx.strokeStyle = '#10b981';
                drawCtx.strokeRect(r.x, r.y, r.w, r.h);
                
                // Identify if this rectangle has an assigned role
                let label = `Face ${index + 1}`;
                if (rectangles.value.length === 6) {
                    const config = faceConfigs.value.find(fc => fc.rectIndex === index);
                    if (config) label = config.role;
                }

                drawCtx.fillStyle = '#10b981';
                drawCtx.fillRect(r.x, r.y, parseInt(drawCtx.measureText(label).width) + (20/scale.value), 20 / scale.value);
                drawCtx.fillStyle = '#fff';
                drawCtx.font = `${12 / scale.value}px Arial text-bold`;
                drawCtx.textBaseline = 'alphabetic';
                drawCtx.fillText(label, r.x + (4 / scale.value), r.y + (14 / scale.value));
            });
        };

        const autoAssignRoles = () => {
            if (rectangles.value.length !== 6) return;

            // 1. Calculate centers and metadata
            const data = rectangles.value.map((r, i) => ({
                index: i,
                cx: r.x + r.w / 2,
                cy: r.y + r.h / 2,
                w: r.w,
                h: r.h
            }));

            // 2. Beam detection (neighbor analysis)
            const getNeighbors = (rect) => {
                const horizontal = data.filter(other => 
                    other.index !== rect.index && 
                    Math.abs(other.cy - rect.cy) < Math.min(rect.h, other.h) * 0.5
                );
                const vertical = data.filter(other => 
                    other.index !== rect.index && 
                    Math.abs(other.cx - rect.cx) < Math.min(rect.w, other.w) * 0.5
                );
                return { horizontal, vertical };
            };

            const enriched = data.map(r => ({ ...r, neighbors: getNeighbors(r) }));

            // 3. Find Top (Anchor)
            // It should have 2 horizontal (Left, Right) and 2 vertical neighbors in column (Back, Front)
            // Or prioritize the one with MOST neighbors.
            let topCandidate = enriched.sort((a, b) => 
                (b.neighbors.horizontal.length + b.neighbors.vertical.length) - 
                (a.neighbors.horizontal.length + a.neighbors.vertical.length)
            )[0];

            if (!topCandidate) return;

            // 4. Extract Row and Column based on the Anchor
            const column = [...topCandidate.neighbors.vertical, topCandidate].sort((a, b) => a.cy - b.cy);
            const row = [...topCandidate.neighbors.horizontal, topCandidate].sort((a, b) => a.cx - b.cx);

            // Expecting column of 3 or 4. If 3, we might need to find the 4th (Bottom) below Front.
            // In the target layout, the column is 4 deep: Back, Top, Front, Bottom.
            // If the user's cross is perfect, column.length === 4 and row.length === 3.
            
            // Re-scan for Bottom if it's not and-aligned with Top but is below Front
            let verticalTrack = column;
            if (verticalTrack.length < 4) {
               // The 4th might be slightly offset. Let's find leftovers.
               const usedIndices = new Set([...column.map(c => c.index), ...row.map(r => r.index)]);
               const leftovers = enriched.filter(e => !usedIndices.has(e.index));
               if (leftovers.length > 0) {
                   // Bottom is likely the lowest leftover
                   const bottom = leftovers.sort((a, b) => b.cy - a.cy)[0];
                   verticalTrack.push(bottom);
                   verticalTrack.sort((a, b) => a.cy - b.cy);
               }
            }

            const assignments = {
                'Top': verticalTrack[0]?.index,
                'Front': verticalTrack[1]?.index,
                'Bottom': verticalTrack[2]?.index,
                'Back': verticalTrack[3]?.index,
                'Left': row[0]?.index,
                'Right': row[2]?.index
            };

            // 5. Update faceConfigs
            faceConfigs.value.forEach(fc => {
                const foundIndex = assignments[fc.role];
                if (foundIndex !== undefined) {
                    fc.rectIndex = foundIndex;
                    const defaultRot = defaultRotations[fc.role] || 0;
                    fc.rotation = ((defaultRot % 360) + 360) % 360; 
                }
            });

            console.log("Auto-assigned roles:", assignments);
        };

        // Save state logic
        const saveMappingToStorage = () => {
            if (currentImageMd5.value && rectangles.value.length > 0) {
                const data = {
                    rectangles: rectangles.value,
                    faceConfigs: faceConfigs.value.map(fc => ({ rectIndex: fc.rectIndex, rotation: fc.rotation, flipX: fc.flipX, flipY: fc.flipY })),
                    viewSettings: {
                        pos: { x: currentPositionX, y: currentPositionY, z: currentPositionZ },
                        rot: { 
                            x: Math.round(THREE.MathUtils.radToDeg(currentRotationX)), 
                            y: Math.round(THREE.MathUtils.radToDeg(currentRotationY)), 
                            z: Math.round(THREE.MathUtils.radToDeg(currentRotationZ)) 
                        },
                        camZoom: camera ? camera.zoom : currentZoom,
                        camFov: camera ? camera.fov : currentFov
                    }
                };
                localStorage.setItem(`3d_mapping_${currentImageMd5.value}`, JSON.stringify(data));
            }
        };

        // Navigation & Wizard Flow
        const onSelectChange = (index) => {
            update3DBox();
        };

        const formatFaceOption = (state) => {
            if (!state.id) return state.text;
            const imgUrl = $(state.element).data('image');
            if (!imgUrl) return state.text;
            
            return $(
                `<span><img src="${imgUrl}" class="select2-option-image" style="width: 24px; height: 24px; margin-right: 10px; vertical-align: middle; border: 1px solid #eee; border-radius: 4px; object-fit: contain; background: #f9f9f9;" /> ${state.text}</span>`
            );
        };

        const initSelect2 = () => {
            nextTick(() => {
                $('.face-select').each(function(index) {
                    const $el = $(this);
                    if ($el.data('select2')) {
                        $el.select2('destroy');
                    }
                    
                    $el.select2({
                        templateResult: formatFaceOption,
                        templateSelection: formatFaceOption,
                        width: '100%',
                        minimumResultsForSearch: Infinity // Hide search box if not needed
                    }).on('select2:select', (e) => {
                        const val = e.params.data.id === "" || e.params.data.id === "null" ? null : parseInt(e.params.data.id);
                        faceConfigs.value[index].rectIndex = val;
                        update3DBox();
                    });
                });
            });
        };

        const goToStep = async (newStep) => {
            if (newStep === 2) loadingStates.nextStep = true;
            if (newStep === 3 && step.value === 2) loadingStates.reviewExport = true;
            
            try {
                step.value = newStep;
                
                await nextTick();
                if (newStep === 2 || newStep === 3) {
                    saveMappingToStorage(); // Save anytime we advance
                    mount3DToCurrentStep();
                    update3DBox();
                    
                    if (newStep === 2) {
                        initSelect2();
                    }
                }
            } finally {
                loadingStates.nextStep = false;
                loadingStates.reviewExport = false;
            }
        };

        // Thumbnails logic
        const clearCache = () => {
            for (let key in cachedThumbs) {
                delete cachedThumbs[key];
            }
        };

        const getFaceThumb = (rectIndex, rotation, flipX = false, flipY = false) => {
            const cacheKey = `${rectIndex}_${rotation}_${flipX}_${flipY}`;
            if (cachedThumbs[cacheKey]) return cachedThumbs[cacheKey];

            const r = rectangles.value[rectIndex];
            if (!r) return null;

            const tempCanvas = document.createElement('canvas');
            const isRotatedSides = (rotation % 180 !== 0);
            tempCanvas.width = isRotatedSides ? Math.round(r.h) : Math.round(r.w);
            tempCanvas.height = isRotatedSides ? Math.round(r.w) : Math.round(r.h);
            
            const tCtx = tempCanvas.getContext('2d');
            tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tCtx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
            tCtx.rotate((rotation * Math.PI) / 180);
            tCtx.drawImage(bgCanvas.value, r.x, r.y, r.w, r.h, -r.w / 2, -r.h / 2, r.w, r.h);

            const dataUrl = tempCanvas.toDataURL();
            cachedThumbs[cacheKey] = dataUrl;
            return dataUrl;
        };

        const rotateFaceConfig = (index, deg) => {
            faceConfigs.value[index].rotation = (faceConfigs.value[index].rotation + deg) % 360;
            saveMappingToStorage();
            update3DBox();
        };

        const flipFaceConfig = (index, axis) => {
            if (axis === 'x') faceConfigs.value[index].flipX = !faceConfigs.value[index].flipX;
            if (axis === 'y') faceConfigs.value[index].flipY = !faceConfigs.value[index].flipY;
            saveMappingToStorage();
            update3DBox();
        };
      
        const exportFaceTexture = (faceIndex) => {
            const config = faceConfigs.value[faceIndex];
            if (config.rectIndex === null || !bgCanvas.value) return;
            const r = rectangles.value[config.rectIndex];
            if (!r) return;

            const { rotation, flipX, flipY } = config;
            const tempCanvas = document.createElement('canvas');
            const isRotatedSides = (rotation % 180 !== 0);
            const actualW = isRotatedSides ? r.h : r.w;
            const actualH = isRotatedSides ? r.w : r.h;

            tempCanvas.width = Math.round(actualW);
            tempCanvas.height = Math.round(actualH);

            const tCtx = tempCanvas.getContext('2d');
            tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tCtx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
            tCtx.rotate((rotation * Math.PI) / 180);
            tCtx.drawImage(bgCanvas.value, r.x, r.y, r.w, r.h, -r.w / 2, -r.h / 2, r.w, r.h);

            const dataURL = tempCanvas.toDataURL('image/png', 1.0);
            const link = document.createElement('a');
            const safeRole = String(config.role).replace(/\s+/g, '_');
            link.download = `3d-face-${safeRole}-rect${config.rectIndex + 1}-${tempCanvas.width}x${tempCanvas.height}.png`;
            link.href = dataURL;
            link.click();
        };

        const createFacePngBlob = async (faceIndex) => {
            const config = faceConfigs.value[faceIndex];
            if (config.rectIndex === null || !bgCanvas.value) return null;
            const r = rectangles.value[config.rectIndex];
            if (!r) return null;

            const { rotation, flipX, flipY } = config;
            const tempCanvas = document.createElement('canvas');
            const isRotatedSides = (rotation % 180 !== 0);
            const actualW = isRotatedSides ? r.h : r.w;
            const actualH = isRotatedSides ? r.w : r.h;

            tempCanvas.width = Math.round(actualW);
            tempCanvas.height = Math.round(actualH);

            const tCtx = tempCanvas.getContext('2d');
            tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tCtx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
            tCtx.rotate((rotation * Math.PI) / 180);
            tCtx.drawImage(bgCanvas.value, r.x, r.y, r.w, r.h, -r.w / 2, -r.h / 2, r.w, r.h);

            const blob = await new Promise((resolve) =>
                tempCanvas.toBlob(resolve, 'image/png', 1.0)
            );
            if (!blob) return null;

            const safeRole = String(config.role).replace(/\s+/g, '_');
            const filename = `3d-face-${safeRole}-rect${config.rectIndex + 1}-${tempCanvas.width}x${tempCanvas.height}.png`;
            return { filename, blob };
        };

        const exportAllFaceTexturesZip = async () => {
            if (loadingStates.facesZip) return;
            loadingStates.facesZip = true;
            try {
                const zip = new JSZip();
                const facesFolder = zip.folder('faces');

                const items = await Promise.all(
                    faceConfigs.value.map((_, idx) => createFacePngBlob(idx))
                );

                for (const item of items) {
                    if (!item) continue;
                    facesFolder.file(item.filename, item.blob);
                }

                const blob = await zip.generateAsync({
                    type: 'blob',
                    compression: 'DEFLATE',
                    compressionOptions: { level: 6 }
                });

                const shortMd5 = currentImageMd5.value ? String(currentImageMd5.value).substring(0, 8) : 'unknown';
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `3d-faces-${shortMd5}.zip`;
                link.click();
                URL.revokeObjectURL(link.href);
            } finally {
                loadingStates.facesZip = false;
            }
        };

        /** 3D LOGIC **/
        const initThreeJS = () => {
            if (scene) return; 

            scene = new THREE.Scene();
            camera = new THREE.PerspectiveCamera(currentFov, 1, 0.1, 1000);
            camera.position.set(0, 1.5, 6);
            camera.zoom = currentZoom;
            camera.lookAt(0, 0, 0);

            renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
            renderer.outputColorSpace = THREE.SRGBColorSpace;
            renderer.setPixelRatio(window.devicePixelRatio);
            
            // Custom interaction logic
            const el = renderer.domElement;
            el.addEventListener('mousedown', (e) => {
                isDraggingObject = true;
                dragMode = (e.button === 2 || e.ctrlKey) ? 'pan' : 'rotate';
                prevMouseX = e.clientX;
                prevMouseY = e.clientY;
            });

            window.addEventListener('mousemove', (e) => {
                if (!isDraggingObject || !cubeMesh) return;
                
                const dx = e.clientX - prevMouseX;
                const dy = e.clientY - prevMouseY;
                
                if (dragMode === 'rotate') {
                    cubeMesh.rotation.y += dx * 0.01;
                    cubeMesh.rotation.x += dy * 0.01;
                    // Sync internal rotation placeholders for buttons
                    currentRotationX = cubeMesh.rotation.x;
                    currentRotationY = cubeMesh.rotation.y;
                    currentRotationZ = cubeMesh.rotation.z;
                } else if (dragMode === 'pan') {
                    const scale = 0.005 * (camera.position.z / 6) / camera.zoom;
                    cubeMesh.position.x += dx * scale;
                    cubeMesh.position.y -= dy * scale;
                    currentPositionX = cubeMesh.position.x;
                    currentPositionY = cubeMesh.position.y;
                }
                
                prevMouseX = e.clientX;
                prevMouseY = e.clientY;
            });

            window.addEventListener('mouseup', () => {
                isDraggingObject = false;
                dragMode = null;
            });

            el.addEventListener('contextmenu', e => e.preventDefault());

            el.addEventListener('wheel', (e) => {
                e.preventDefault();
                const factor = e.deltaY > 0 ? 0.9 : 1.1;
                camera.zoom = Math.max(0.1, Math.min(camera.zoom * factor, 20));
                currentZoom = camera.zoom;
                camera.updateProjectionMatrix();
            }, { passive: false });

            animate();
        };

        const mount3DToCurrentStep = () => {
            initThreeJS();
            const container = step.value === 2 ? threeContainerStep2.value : threeContainerStep3.value;
            if (container && renderer) {
                container.appendChild(renderer.domElement);
                onWindowResize();
            }
        };

        const onWindowResize = () => {
            const container = step.value === 2 ? threeContainerStep2.value : threeContainerStep3.value;
            if (camera && renderer && container) {
                camera.aspect = container.clientWidth / container.clientHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(container.clientWidth, container.clientHeight);
            }
        };

        const animate = () => {
            animationId = requestAnimationFrame(animate);
            if (renderer && scene && camera) {
                renderer.render(scene, camera);
                
                // Update stats
                if (cubeMesh) {
                    meshStats.pos.x = cubeMesh.position.x.toFixed(2);
                    meshStats.pos.y = cubeMesh.position.y.toFixed(2);
                    meshStats.pos.z = cubeMesh.position.z.toFixed(2);
                    
                    meshStats.rot.x = Math.round(THREE.MathUtils.radToDeg(cubeMesh.rotation.x));
                    meshStats.rot.y = Math.round(THREE.MathUtils.radToDeg(cubeMesh.rotation.y));
                    meshStats.rot.z = Math.round(THREE.MathUtils.radToDeg(cubeMesh.rotation.z));
                }
                
                camStats.zoom = camera.zoom.toFixed(2);
                camStats.fov = Math.round(camera.fov);
            }
        };

        const zoom3DIn = () => {
            if (camera) {
                camera.zoom = Math.min(20, camera.zoom * 1.2);
                currentZoom = camera.zoom;
                camera.updateProjectionMatrix();
            }
        };

        const zoom3DOut = () => {
            if (camera) {
                camera.zoom = Math.max(0.1, camera.zoom * 0.8);
                currentZoom = camera.zoom;
                camera.updateProjectionMatrix();
            }
        };

        const reset3DView = () => {
            currentPositionX = 0.08;
            currentPositionY = 0.14;
            currentPositionZ = 0.00;
            currentRotationX = THREE.MathUtils.degToRad(90);
            currentRotationY = THREE.MathUtils.degToRad(0);
            currentRotationZ = THREE.MathUtils.degToRad(-15);
            currentZoom = 0.54;
            currentFov = 10;

            if (camera && cubeMesh) {
                cubeMesh.position.set(currentPositionX, currentPositionY, currentPositionZ);
                cubeMesh.rotation.set(currentRotationX, currentRotationY, currentRotationZ);
                
                camera.zoom = currentZoom;
                camera.fov = currentFov;
                camera.updateProjectionMatrix();
            }
        };

        const changeFOV = (amount) => {
            if (camera) {
                camera.fov = Math.max(10, Math.min(150, camera.fov + amount)); 
                currentFov = camera.fov;
                camera.updateProjectionMatrix();
            }
        };

        const rotateMesh = (axis, dir) => {
            if (!cubeMesh) return;
            const amount = dir * Math.PI / 12;
            if (axis === 'x') {
                cubeMesh.rotation.x += amount;
                currentRotationX = cubeMesh.rotation.x;
            }
            if (axis === 'y') {
                cubeMesh.rotation.y += amount;
                currentRotationY = cubeMesh.rotation.y;
            }
            if (axis === 'z') {
                cubeMesh.rotation.z += amount;
                currentRotationZ = cubeMesh.rotation.z;
            }
        };

        const update3DBox = () => {
            if (!scene) return;
            
            if (cubeMesh) {
                scene.remove(cubeMesh);
                cubeMesh.geometry.dispose();
                cubeMesh.material.forEach(mat => {
                    if (mat.map) mat.map.dispose();
                    mat.dispose();
                });
            }

            const materials = [];
            const widths = [];
            const heights = [];
            const depths = [];

            for (let i = 0; i < 6; i++) {
                const config = faceConfigs.value[i];
                if (config.rectIndex !== null && rectangles.value[config.rectIndex]) {
                    const r = rectangles.value[config.rectIndex];
                    
                    const tempCanvas = document.createElement('canvas');
                    const isRotatedSides = (config.rotation % 180 !== 0);
                    const actualW = isRotatedSides ? r.h : r.w;
                    const actualH = isRotatedSides ? r.w : r.h;

                    tempCanvas.width = actualW;
                    tempCanvas.height = actualH;
                    
                    const tCtx = tempCanvas.getContext('2d');
                    tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
                    tCtx.scale(config.flipX ? -1 : 1, config.flipY ? -1 : 1);
                    tCtx.rotate((config.rotation * Math.PI) / 180);
                    tCtx.drawImage(bgCanvas.value, r.x, r.y, r.w, r.h, -r.w / 2, -r.h / 2, r.w, r.h);
                    
                    const texture = new THREE.CanvasTexture(tempCanvas);
                    // Enable high quality texture filtering
                    texture.minFilter = THREE.LinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.generateMipmaps = false; // Disable mipmaps for sharper 2D extraction
                    texture.colorSpace = THREE.SRGBColorSpace;
                    materials.push(new THREE.MeshBasicMaterial({ map: texture }));

                    // Extract dimensions based on face role
                    // New mapping to preserve "logic":
                    // Front (Center) -> old Top logic (Index 2) -> Depth
                    // Top (Topmost) -> old Back logic (Index 5) -> Height
                    // Bottom (Below center) -> old Front logic (Index 4) -> Height
                    // Back (Bottommost) -> old Bottom logic (Index 3) -> Depth
                    if (config.role === 'Back' || config.role === 'Top' || config.role === 'Bottom' || config.role === 'Front' || config.role === 'Right' || config.role === 'Left') {
                        // We use a role-based approach to ensure labels don't break dimensions
                        if (config.role === 'Front' || config.role === 'Back') {
                            widths.push(actualW);
                            depths.push(actualH); // Center and Bottommost affect Depth in old logic
                        } else if (config.role === 'Top' || config.role === 'Bottom') {
                            widths.push(actualW);
                            heights.push(actualH); // Topmost and Below-center affect Height in old logic
                        } else if (config.role === 'Right' || config.role === 'Left') {
                            depths.push(actualW);
                            heights.push(actualH);
                        }
                    }
                } else {
                    // Different subtle colors per face if empty to help orientation
                    const colors = [0xffcccc, 0xccffcc, 0xccccff, 0xffffcc, 0xffccff, 0xccffff];
                    materials.push(new THREE.MeshBasicMaterial({ color: colors[i], wireframe: true }));
                }
            }

            // Calculate average dimensions from all contributors
            const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 150;
            
            const rawWidth = avg(widths);
            const rawHeight = avg(heights);
            const rawDepth = avg(depths);

            const maxDim = Math.max(rawWidth, rawHeight, rawDepth);
            const scaleToFit = 2 / maxDim;

            const boxWidth = rawWidth * scaleToFit;
            const boxHeight = rawHeight * scaleToFit;
            const boxDepth = rawDepth * scaleToFit;

            const geometry = new THREE.BoxGeometry(boxWidth, boxHeight, boxDepth);
            cubeMesh = new THREE.Mesh(geometry, materials);
            cubeMesh.position.set(currentPositionX, currentPositionY, currentPositionZ);
            cubeMesh.rotation.set(currentRotationX, currentRotationY, currentRotationZ);
            scene.add(cubeMesh);
        };

        const openSelectFolderModal = () => {
          expectingMappingJsonFromBrowser.value = false;
          proxy.$bvModal.show('select-folder-modal');
        };

        const openSelectFolderModalForMappingJson = () => {
          expectingMappingJsonFromBrowser.value = true;
          proxy.$bvModal.show('select-folder-modal');
        };

        const fileBrowserItemSelected = async (items) => {
          if (!items.length) return;

          if (expectingMappingJsonFromBrowser.value) {
            expectingMappingJsonFromBrowser.value = false;
            const item = items[0];
            if (item.type === FILE_TYPE.FOLDER) {
              alert('Please select a .json mapping file, not a folder.');
              return;
            }
            const url = item.path;
            if (!url) return;

            loadingStates.loadFile = true;
            try {
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status}`);
              }
              const text = await response.text();
              let data;
              try {
                data = JSON.parse(text);
              } catch {
                alert('Selected file is not valid JSON.');
                return;
              }
              if (typeof data !== 'object' || data === null || !('rectangles' in data)) {
                alert('Selected file does not look like a 3D mappings export.');
                return;
              }
              if (data.md5 && data.md5 !== currentImageMd5.value) {
                if (!confirm('The selected mapping file was created for a different image (MD5 mismatch). Load anyway?')) {
                  return;
                }
              }
              await applyMappingData(data);
            } catch (err) {
              alert('Could not load mapping file: ' + err.message);
            } finally {
              loadingStates.loadFile = false;
            }
            return;
          }

          const url = items[0].path;
          if (!url) return;

          isImageLoading.value = true;

          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.status}`);
            }

            const blob = await response.blob();

            const arrayBuffer = await blob.arrayBuffer();
            const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
            const md5hash = CryptoJS.MD5(wordArray).toString();

            currentImageMd5.value = md5hash;

            const objectUrl = URL.createObjectURL(blob);

            uploadedImage.onload = () => {
              handleImageLoad(md5hash);
              URL.revokeObjectURL(objectUrl);
            };

            uploadedImage.onerror = () => {
              isImageLoading.value = false;
              URL.revokeObjectURL(objectUrl);
            };

            uploadedImage.src = objectUrl;
          } catch (error) {
            console.error('Error loading image from URL:', error);
            isImageLoading.value = false;
          }
        }

        const export3DImage = () => {
            if (!scene || !camera) return;
            
            const container = step.value === 2 ? threeContainerStep2.value : threeContainerStep3.value;
            const aspect = container.clientWidth / container.clientHeight;

            // Create a temporary high-resolution renderer
            const maxDimension = 7680; // 8K maximum dimension
            let exportWidth, exportHeight;
            
            if (aspect >= 1) {
                exportWidth = maxDimension;
                exportHeight = Math.round(maxDimension / aspect);
            } else {
                exportHeight = maxDimension;
                exportWidth = Math.round(maxDimension * aspect);
            }
            
            const tempRenderer = new THREE.WebGLRenderer({ 
                antialias: true, 
                alpha: true, 
                preserveDrawingBuffer: true 
            });
            
            // Configure for maximum quality
            // Enforce pixel ratio 1 so the internal canvas buffer size is exactly exportWidth x exportHeight.
            // Using window.devicePixelRatio here can multiply the 8K resolution, exceeding browser canvas size limits on high-DPI screens.
            tempRenderer.setPixelRatio(1);
            tempRenderer.setSize(exportWidth, exportHeight);
            
            // Clone the camera to match current view but update aspect ratio for 8K
            const tempCamera = camera.clone();
            tempCamera.aspect = exportWidth / exportHeight;
            tempCamera.updateProjectionMatrix();

            // Render to the temporary canvas
            tempRenderer.render(scene, tempCamera);
            
            // Crop the rendered canvas to non-transparent pixels with 32px padding
            const sourceCanvas = tempRenderer.domElement;
            const w = sourceCanvas.width;
            const h = sourceCanvas.height;
            
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = w;
            tempCanvas.height = h;
            const ctx = tempCanvas.getContext('2d');
            ctx.drawImage(sourceCanvas, 0, 0);
            
            const imageData = ctx.getImageData(0, 0, w, h);
            const data = imageData.data;
            
            let top = 0, bottom = h - 1, left = 0, right = w - 1;
            
            const isRowEmpty = (y) => {
                for (let x = 0; x < w; x++) {
                    if (data[(y * w + x) * 4 + 3] > 0) return false;
                }
                return true;
            };
            
            const isColEmpty = (x, topY, bottomY) => {
                for (let y = topY; y <= bottomY; y++) {
                    if (data[(y * w + x) * 4 + 3] > 0) return false;
                }
                return true;
            };
            
            // Find actual bounds
            while (top < h && isRowEmpty(top)) top++;
            
            let finalCanvas = sourceCanvas; // Default to uncropped if fully transparent
            
            if (top < h) {
                while (bottom > top && isRowEmpty(bottom)) bottom--;
                while (left < w && isColEmpty(left, top, bottom)) left++;
                while (right > left && isColEmpty(right, top, bottom)) right--;
                
                const padding = 32;
                const trimWidth = right - left + 1;
                const trimHeight = bottom - top + 1;
                
                const trimmedCanvas = document.createElement('canvas');
                trimmedCanvas.width = trimWidth + (padding * 2);
                trimmedCanvas.height = trimHeight + (padding * 2);
                
                const trimmedCtx = trimmedCanvas.getContext('2d');
                trimmedCtx.drawImage(
                    sourceCanvas,
                    left, top, trimWidth, trimHeight,
                    padding, padding, trimWidth, trimHeight
                );
                
                finalCanvas = trimmedCanvas;
            }

            // Export as maximum quality PNG
            const dataURL = finalCanvas.toDataURL("image/png", 1.0);
            
            // Cleanup
            tempRenderer.dispose();
            
            // Trigger download
            const link = document.createElement("a");
            link.download = "3d-texture-render-8k.png";
            link.href = dataURL;
            link.click();
        };

        const toggleDropdown = (index) => {
            activeDropdown.value = activeDropdown.value === index ? null : index;
        };

        const selectFace = (faceIndex, rectIndex) => {
            faceConfigs.value[faceIndex].rectIndex = rectIndex;
            activeDropdown.value = null;
            update3DBox();
        };

        return {
            step, goToStep,
            fileInput, viewport, bgCanvas, drawCanvas,
            threeContainerStep2, threeContainerStep3,
            rectangles, scale, panX, panY, transformStyle, cursorClass,
            onFileChange, onMouseDown, onWheel, applyZoom, clearSelection,
            faceConfigs, rotateFaceConfig, flipFaceConfig, getFaceThumb, exportFaceTexture, exportAllFaceTexturesZip, update3DBox,
            zoom3DIn, zoom3DOut, changeFOV, rotateMesh, export3DImage, reset3DView,
            onSelectChange,
            
            // Video Export
            showVideoModal, isVideoGenerating, videoProgress, videoSettings,
            openVideoModal, generateVideo, abortVideoGeneration,

            // Persistence
            showRecoveryModal, tempMappingData, loadSavedMapping, cancelRecovery,
            triggerMappingFileClick, onMappingFileChange, exportMappings, mappingFileInput,

            // Wizard Sequence
            rolePromptOrder, uploadedImageSrc, currentPromptFace, showOverlayPrompt,
            camStats, meshStats,
            // Drag & Drop
            isDragging, onDrop, onDragOver, onDragLeave, triggerFileInput,
            isImageLoading, loadingStates,
            activeDropdown, toggleDropdown, selectFace, setResolution, openSelectFolderModal, openSelectFolderModalForMappingJson, fileBrowserItemSelected,
            rectanglesMapped: computed(() => rectangles.value.length)
        };
    }
}
</script>

<style scoped>
:host {
    --brand-primary: #209C86;
    --brand-dark: #197d6c;
    --brand-light: #f0fdfa;
    --brand-border: #ccfbf1;
}

.text-brand { color: #209C86; }
.bg-brand { background-color: #209C86; }
.bg-brand-light { background-color: #f0fdfa; }
.border-brand { border-color: #209C86; }
.btn-brand {
    background-color: #209C86;
    color: white;
}
.btn-brand:hover {
    background-color: #197d6c;
    color: white;
}
.btn-brand:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
}

.loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.brand-spinner {
    width: 64px;
    height: 64px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid var(--brand-primary, #209C86);
    border-radius: 50%;
    animation: brand-spin 1s linear infinite;
}

@keyframes brand-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.button-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid #fff;
    border-radius: 50%;
    animation: brand-spin 0.6s linear infinite;
    display: inline-block;
}

#canvas-viewport { 
    user-select: none; 
    -webkit-user-select: none;
    touch-action: none;
}

::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #f3f4f6; }
::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

.wizard-step {
    transition: all 0.3s ease;
}

/* Custom Face Selection Dropdown Styles */
.custom-dropdown-container {
    user-select: none;
}

.dropdown-toggle-custom {
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-dropdown-menu {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.dropdown-item-custom {
    transition: all 0.15s ease;
}

/* Per-face export: hidden until row hover (or keyboard focus). */
.tdr-face-export-btn {
    width: 2rem;
    height: 2rem;
    border-radius: 0.375rem;
    border: 1px solid #e5e7eb;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    opacity: 0;
}
.tdr-face-config-row:hover .tdr-face-export-btn,
.tdr-face-config-row:focus-within .tdr-face-export-btn {
    opacity: 1;
}
.tdr-face-config-row:hover .tdr-face-export-btn:not(:disabled),
.tdr-face-config-row:focus-within .tdr-face-export-btn:not(:disabled) {
    background-color: #eef2f6;
    border-color: #cfd8e3;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    outline: none;
}
.tdr-face-export-all-btn {
    opacity: 1; /* Always visible in panel header */
}
.tdr-face-export-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}
.tdr-face-export-btn:not(:disabled):hover,
.tdr-face-export-btn:not(:disabled):focus-visible {
    cursor: pointer;
}
.tdr-face-export-btn:not(:disabled):hover,
.tdr-face-export-btn:not(:disabled):focus-visible {
    background-color: #eef2f6;
    border-color: #cfd8e3;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    outline: none;
}
.tdr-face-export-btn:not(:disabled):focus-visible {
    box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.35), 0 1px 4px rgba(0, 0, 0, 0.08);
}

.tdr-face-actions {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}
.tdr-face-actions .tdr-face-export-btn-float {
    position: absolute;
    right: 0;
    top: -38px;
}
.tdr-face-export-btn.is-disabled {
    pointer-events: none;
    opacity: 0;
}

.tdr-face-action-btn {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.375rem;
    border: 1px solid #e5e7eb;
    background-color: #eef2f6;
    color: #374151;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.tdr-face-action-btn:hover {
    background-color: #e5e7eb;
    border-color: #d1d5db;
}
.tdr-face-action-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.35), 0 1px 4px rgba(0, 0, 0, 0.08);
}
.tdr-face-action-btn.is-active {
    background-color: #209c86;
    border-color: #1b8a77;
    color: #ffffff;
}

.face-thumb-mini {
    width: 32px;
    height: 32px;
}

.extra-small {
    font-size: 10px;
}


/* Select2 Custom Styles to match Tailwind/Gray theme */
:deep(.select2-container--default .select2-selection--single) {
    height: 38px;
    display: flex;
    align-items: center;
    border-color: #d1d5db;
    border-radius: 0.375rem;
    background-color: #fff;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    color: #111827;
    font-size: 0.875rem;
    line-height: normal;
    display: flex;
    align-items: center;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    height: 36px;
}
:deep(.select2-dropdown) {
    border-color: #d1d5db;
    border-radius: 0.375rem;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
    overflow: hidden;
}
:deep(.select2-results__option) {
    padding: 6px 12px;
    font-size: 0.875rem;
}
:deep(.select2-container--default .select2-results__option--highlighted[aria-selected]) {
    background-color: #2563eb;
}

/* Large overlay text fade-out animation */
.fade-out {
    animation: fadeout 1.5s ease-out forwards;
    pointer-events: none;
}

@keyframes fadeout {
    0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
}
::v-deep .custom-secondary-btn {
  border: 1px solid #e2e8f0;
  background-color: #fff;
  border-radius: 6px;
}
.custom-secondary-btn:hover:not(:disabled) {
  background-color: #f1f5f9;
}
.custom-secondary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
